import type { PeerDiagnostics } from "@codeshare/session";
import { useEffect, useRef, useState } from "react";
import { BUILD_INFO } from "../build-info";
import { describePath, diagnosticsReport } from "../diagnostics/report";
import { getRoomController } from "../session/use-room";
import { closeOnBackdrop } from "./dialog";

const kb = (bytes: number | undefined) =>
  bytes === undefined ? "–" : bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;

/** Connection details per peer, from WebRTC stats, refreshed while open; copyable as JSON. */
export function Diagnostics() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const [peers, setPeers] = useState<PeerDiagnostics[]>([]);
  const [copied, setCopied] = useState<string>();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const refresh = async () => {
      const result = (await getRoomController().room?.diagnostics()) ?? [];
      if (!cancelled) setPeers(result);
    };
    void refresh();
    const timer = setInterval(() => void refresh(), 2_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [open]);

  const report = () => {
    const rooms = getRoomController();
    return diagnosticsReport({
      app: { version: BUILD_INFO.version, sha: BUILD_INFO.sha, builtAt: BUILD_INFO.builtAt },
      status: rooms.state.status,
      config: rooms.config,
      peers,
      environment: {
        userAgent: navigator.userAgent,
        crossOriginIsolated: globalThis.crossOriginIsolated ?? false,
        online: navigator.onLine,
      },
    });
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(report());
      setCopied("Copied");
    } catch {
      setCopied("Couldn't copy (clipboard unavailable)");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setCopied(undefined);
          setOpen(true);
          dialogRef.current?.showModal();
        }}
      >
        Connection details
      </button>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes dialogs natively; this only adds backdrop clicks for the mouse */}
      <dialog
        ref={dialogRef}
        onClick={closeOnBackdrop}
        className="dialog diagnostics-dialog"
        aria-label="Connection details"
        onClose={() => setOpen(false)}
      >
        <h2>Connection details</h2>
        {peers.length === 0 && <p className="dialog-hint">Not connected to anyone yet.</p>}
        {peers.map((peer) => (
          <section key={peer.id} className="diagnostics-peer" aria-label={peer.name ?? peer.id}>
            <h3>
              {peer.name ?? "Connecting…"} <span className="peer-meta">({peer.state})</span>
            </h3>
            <dl>
              <dt>Path</dt>
              <dd data-testid="diagnostics-path">
                {describePath(peer.connection.pair, peer.connection)}
              </dd>
              <dt>Round trip</dt>
              <dd>
                {peer.rtt !== undefined ? `${peer.rtt} ms` : "–"}
                {peer.connection.pair?.rtt !== undefined &&
                  ` (ICE ${Math.round(peer.connection.pair.rtt * 1000)} ms)`}
              </dd>
              <dt>Traffic</dt>
              <dd>
                ↑ {kb(peer.connection.pair?.bytesSent)} · ↓{" "}
                {kb(peer.connection.pair?.bytesReceived)}
              </dd>
              {peer.app && (
                <>
                  <dt>Their build</dt>
                  <dd>
                    v{peer.app.version} ({peer.app.sha})
                  </dd>
                </>
              )}
            </dl>
            <table className="diagnostics-channels">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>State</th>
                  <th>Sent</th>
                  <th>Received</th>
                  <th>Queued</th>
                </tr>
              </thead>
              <tbody>
                {peer.connection.channels.map((c) => (
                  <tr key={`${c.label}/${c.protocol}`}>
                    <td>{c.protocol || c.label}</td>
                    <td>{c.state}</td>
                    <td>
                      {c.messagesSent} · {kb(c.bytesSent)}
                    </td>
                    <td>
                      {c.messagesReceived} · {kb(c.bytesReceived)}
                    </td>
                    <td>{kb(c.bufferedAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
        <p className="dialog-hint">
          The copied report has no room link, secret, credentials or addresses.
        </p>
        <div className="dialog-actions">
          {copied && (
            <span className="dialog-hint" role="status">
              {copied}
            </span>
          )}
          <button type="button" onClick={() => void copy()}>
            Copy diagnostics
          </button>
          <button type="button" className="primary" onClick={() => dialogRef.current?.close()}>
            Close
          </button>
        </div>
      </dialog>
    </>
  );
}
